
import { DocumentReference } from 'firebase/firestore';

export enum itemSize {
	tiny = 'tiny',
	small = 'small',
	medium = 'medium',
	large = 'large',
	huge = 'huge',
}

/**
 * How long one loaded charge of an item lasts, anchored to Cybercore's
 * narrative time scales (see content/.../1.cybersystem/7.actions.md and
 * 11.resting.md for the Round/Encounter/Op/Mission ladder).
 *
 * Composes with `uses` + `usesType`: `uses` is the count of "drain
 * events" one loaded consumable supplies, and `drainCadence` defines
 * what a "drain event" means in time terms. Examples:
 *   - Plasma carbine: uses=30, usesType="E-Cell", drainCadence=perShot
 *   - Flashlight: uses=1, usesType="E-Cell", drainCadence=perOp
 *   - Personal shield: uses=1, usesType="E-Cell", drainCadence=perEncounter
 *   - AI implant: passive (no uses, drains nothing in play)
 */
export enum drainCadence {
    passive = 'passive',           // Always on, no drain in play
    perShot = 'per-shot',          // One drain event per activation (weapons, welders)
    perRound = 'per-round',        // One drain event per combat round of active use
    perEncounter = 'per-encounter', // One charge lasts the encounter; refresh at Stim Break
    perOp = 'per-op',              // One charge lasts an Op; refresh at Lockdown
    perMission = 'per-mission',    // One charge lasts a Mission (~3 ops); refresh at R&R
}

export enum itemType {
	equip = 'equip',
	ammo = 'ammo',
	accessory = 'accessory',
	weaponAccessory = 'weaponAccessory',
	tool = 'tool',
	ai = 'ai',
	stims = 'stims',
	aionics = 'aionics',         // AI implants (formerly stored in `bionics`)
	bionics = 'bionics',         // biological prosthesis replacing a limb
	cybernetics = 'cybernetics', // mechanical augmentation
	wetware = 'wetware',         // bio-augmentation (genetic / nanite)
	weapon = 'weapon',
	armour = 'armour',
}

export enum itemGrade {
	personal = 'personal',
	restricted = 'restricted',
	military = 'military'
}

/**
 * Item Security Level — the corporate clearance gate on *purchasing*.
 * Canonical scale (0–5) defined in
 * content/.../3.equipment/3.security-level.md.
 *
 * Two derived views, both centralised in the helpers below so the editor,
 * filter, Foundry export and back-fill scripts never re-derive the mapping:
 *   - Rank: 1 Sec Level per 2 ranks (Std Sec Level = ceil(Rank / 2)).
 *   - Tier of play / itemGrade (even 2-level bands):
 *       Sec 0–1 → Tier 1 / personal   (Ranks 1–2, Sec 0 = any rank)
 *       Sec 2–3 → Tier 2 / restricted (Ranks 3–6)
 *       Sec 4–5 → Tier 3 / military   (Ranks 7–10)
 *
 * The SysOps hacking Access Tokens share these tier bands: a Basic token
 * cracks Sec 1 (Tier 1), Advanced Sec 2–3 (Tier 2), Skeleton Key Sec 4–5
 * (Tier 3), Zero-Day bypasses all. So the whole ladder lines up:
 * Rank → Sec Level → Grade → Tier of play → Token tier.
 */
export const SEC_LEVEL_MIN = 0;
export const SEC_LEVEL_MAX = 5;

/** Clamp any numeric input to the valid 0–5 Sec Level range. */
export const clampSecLevel = (sec: number): number =>
	Math.max(SEC_LEVEL_MIN, Math.min(SEC_LEVEL_MAX, Math.round(Number(sec) || 0)));

/** Grade (tier of play) a Sec Level collapses onto. */
export const secLevelToGrade = (sec: number): itemGrade => {
	const s = clampSecLevel(sec);
	if (s >= 4) return itemGrade.military;
	if (s >= 2) return itemGrade.restricted;
	return itemGrade.personal;
};

/** Tier of play (1–3) a Sec Level collapses onto. */
export const secLevelToTier = (sec: number): 1 | 2 | 3 => {
	const s = clampSecLevel(sec);
	if (s >= 4) return 3;
	if (s >= 2) return 2;
	return 1;
};

/**
 * Lowest character Rank that can purchase gear at this Sec Level
 * (1 Sec Level per 2 ranks). Sec 0 is unrestricted (any rank → 0).
 */
export const secLevelMinRank = (sec: number): number => {
	const s = clampSecLevel(sec);
	return s <= 0 ? 0 : s * 2 - 1;
};

/** Highest Sec Level a character of the given Rank may purchase (Std Sec Level). */
export const rankToStdSecLevel = (rank: number): number =>
	Math.min(SEC_LEVEL_MAX, Math.ceil(Math.max(0, Number(rank) || 0) / 2));

export enum weaponType {
	slug = 'slug',
	laser = 'laser',
	blaster = 'blaster',
	melee = 'melee',
	exotic = 'exotic',
	explosive = 'explosive',
}

export enum weaponClass {
	sidearm = 'sidearm',
	standard = 'standard',
	heavy = 'heavy',
	mounted = 'mounted',
}

export enum weaponDamage {
	blunt = 'blunt',
	pierce = 'pierce',
	slash = 'slash',
	thermal = 'thermal',
	laser = 'laser',
	radiation = 'radiation',
	electrical = 'electrical',
	chemical = 'chemical',
	grav = 'grav',
}

export enum aoeShape {
	radius = 'radius',
	cone = 'cone',
	line = 'line',
}

export interface ItemAoE {
	size: number;
	shape: aoeShape;
}

/**
 * Glyph layer stamped onto an item's image/token by the Itemizer (GH #24).
 *
 * The glyph is baked into the override WebP for read speed, but the spec
 * is kept so the Itemizer can reload + recolour the same glyph later, or
 * re-stamp it onto a freshly generated base. `glyphId` is the SVG filename
 * under `public/svg/Itemizer/`; colours map onto the compositor's paint
 * rules; `anchor`/`scale` position the glyph on the 1000×1000 canvas.
 */
export interface GlyphSpec {
	glyphId: string;
	fill?: string;
	stroke?: string;
	bg?: string;
	anchor?: 'br' | 'bl' | 'tr' | 'tl' | 'center';
	/** Glyph size as a fraction of the canvas. Default 0.5 (bottom-right badge). */
	scale?: number;
}

/**
 * Which image is the active stamp target in the Itemizer, and the source
 * of an item's imageURL/tokenUrl:
 *  - 'family' (or undefined): denormalised from visualFamily; the pipeline
 *    may overwrite it on the next sync.
 *  - 'override': a per-item stamped image authored in the Itemizer. The
 *    pipeline's family→item sync (step 01) MUST NOT clobber it.
 */
export type ImageSource = 'family' | 'override';

// Extending Item interface to include weapon properties
export interface Item {
	item_id: undefined | string;
	name: string;
	description: string;
	type: itemType;
	tag: string;
	credits: number;
	uses: number;
	usesType: string;
	usesSizeMax: string;
	quality: number;
	useActions: number;
	rangeStd: number;
	rangeMax: number;
	equipSlot: number;
	webURL: string;
	imageURL: string;
	tokenUrl: string;
	// Canonical 1024 PNG (charcoal background). Denormalised from the
	// item's visualFamily.rawImageURL. Used as the source for imageURL /
	// tokenUrl derivatives by the image pipeline.
	rawImage?: string;
	itemSize: itemSize;
	/**
	 * Huge-tier scale N (emplacements/machinery). 1 = base Huge (4 Large,
	 * 1x1 hex); N gives an NxN map footprint and 4xN Large capacity.
	 * Ignored for non-Huge sizes. Optional; defaults to 1.
	 */
	sizeScale?: number;
	itemGrade: itemGrade;
	/**
	 * Purchasing clearance gate, 0–5. See {@link secLevelToGrade} /
	 * {@link secLevelMinRank} for how it maps onto itemGrade (tier of play)
	 * and character Rank. Canonical rules: 3.equipment/3.security-level.md.
	 */
	secLevel: number;
	/**
	 * Cybercore-time-anchored drain cadence for the item's loaded charge.
	 * See {@link drainCadence}.
	 */
	drainCadence: drainCadence;
	// Continuous-drain multiplier for "running" devices (torches, scanners,
	// powered armour systems, continuous-fire weapon modes). 0 = no
	// continuous drain. N>0 = 30/N minutes per installed E-Cell charge
	// while `lit` (or equivalent running flag). The drain-scheduler in
	// foundary_vtt/module/lighting/drain-scheduler.mjs reads this together
	// with the installed child E-Cell (containerId).
	drain: number;
	// Fixed self-consumption duration in minutes for one-shot lights /
	// chemicals (flares, glow-sticks). >0 = item burns itself out after N
	// minutes lit; no E-Cell child needed. 0 = uses E-Cell + drain instead.
	selfBurnMinutes: number;
	// Firestore docId of the E-Cell template that this item ships with
	// when added to an actor's inventory. Resolved by the Foundry item-
	// import / on-createItem hook, which clones the cell as a child
	// (containerId = this item) with `uses` set to the template's
	// capacity. Empty = no starter cell.
	starterCellId: string;
	/**
	 * Pharmaceutical intake-safety limit: max doses of this item one
	 * character can take per Op without ill effect. 0 means no limit
	 * (default for non-pharmaceutical items).
	 */
	maxDosesPerOp: number;
	dice: string;
	diceDescription: string;
	attributes: any[];
	property: DocumentReference[];
	tokenId: string;
	weaponType?: weaponType;
	weaponClass?: weaponClass;
	damageType?: weaponDamage;
	hitRating?: number;
	hits?: string; // Hit notation e.g. "2P", "1S+1T", "1B+1P+1T+1C"
	reloadActions?: number;
	targetsMax?: number;
	altShots?: number;                 // Alt-fire shot pool (e.g. underbarrel grenade launcher); default 1.
	aoe?: ItemAoE;                     // Area of effect — size in squares (2m each), shape for Foundry templates.
	mark?: number;                     // Mk-N for Mark-scaled items (AI Aim Assist, AI Enhancers, etc.); 1-5.
	// Universal "this item is offline" state. When true, the item's
	// properties + effects are filtered out of the active pipeline
	// (attack rolls, skill bonuses, mod stacks). EMP, hacking, and the
	// quality-0 broken state all flip this. `disableDuration` is the
	// remaining-rounds counter (0 means indefinite / until repaired).
	disabled?: boolean;
	disableDuration?: number;
	icon_id?: number;
	// Visual-family slug — references item_visual/{slug}. All items in
	// a family (tier/mark variants) share one AI-generated image whose
	// URLs (imageURL/tokenUrl/rawImage) are denormalised from the family
	// doc at generation time. Set by imagePipeline/scripts/buildVisualFamilies.ts.
	visualFamily?: string;
	// --- Itemizer override + glyph layer (GH #24) ---
	// 'override' means imageURL/tokenUrl hold a per-item stamped image that
	// the pipeline's family→item sync MUST NOT clobber. 'family'/undefined =
	// denormalised from visualFamily.
	imageSource?: ImageSource;
	// Baked glyph composite reference (reloadable) + the spec used to build
	// it, so the Itemizer can recolour / re-stamp without re-deriving.
	glyphURL?: string;
	glyphSpec?: GlyphSpec;
	// Ownership & visibility
	public: boolean;
	createdBy: string;
	/** Canonical owner UID. Mirrors createdBy on new items; transfers to
	 *  SYSTEM_OWNER_UID when the item is made Public or its author deletes
	 *  their profile. Optional for backward compatibility. */
	ownerId?: string;
	/** Workflow state, mirrors PropertyVisibility. `public` stays the legacy
	 *  gate; visibility adds the PendingBeta/Beta tiers for the approval flow.
	 *  Absent = treat as public/private per the `public` flag. */
	visibility?: number;
	// Container fields
	isContainer: boolean;
	containerCapacity: number;
	quickAccess: boolean;              // container only: drawing an item out is a free action in combat
	// Enabler system — items that reveal/unlock other inventory slots
	tags: string[];                    // ["holster", "gloves", "belt", "helmet"], slot gating key
	enablesSlots: Record<string, { size: string; type: string }> | null;  // slotId → constraints
	equippingHides: boolean;           // true = item visually vanishes, stacks under next item
	// Integrated-hardware system — slots consumed by the item's built-in
	// kit (e.g. Powered Utility Belt claims its own 3 back-util slots for
	// support systems). Slots in this list render visible-but-occluded:
	// can't accept drops. `occupiesSlotsHelp` surfaces on hover.
	occupiesSlots: string[];
	occupiesSlotsHelp: string;
	// Wetware quirk pool — ordered (mildest → most severe) array of
	// quirk Property document IDs. Wetware-only. At install / upgrade,
	// roll 1d{Mark} against the slice quirkPool[0..Mark-1] to assign
	// the actor instance a quirk. See content/.../gear-list/wetware.md.
	quirkPool?: string[];
	// Weapon-mod system — typed sub-slots inside a weapon. Each mount
	// declares which mod identity tags it accepts. Weapon-only.
	mounts?: Record<string, { label?: string; accepts: string[] }> | null;
	// Mod occupancy — when this item is installed in a weapon (containerId
	// points at the weapon), mountId names which of the weapon's mounts
	// it occupies. Gear-only.
	mountId?: string | null;
}

/**
 * Build a fresh default Item. Type-aware: weapon-only fields
 * (weaponType / weaponClass / damageType / hitRating / hits /
 * reloadActions / targetsMax / altShots) are ONLY populated when
 * `type === itemType.weapon`. For every other type they're omitted,
 * keeping the Firestore document clean and avoiding the periodic
 * "clear weaponType+damageType on N non-weapon items" cleanup sweeps.
 *
 * Passing no argument defaults to `itemType.equip` (most common), which
 * also omits weapon fields. Callers creating a weapon should pass
 * `itemType.weapon` so the sensible weapon defaults are populated.
 */
export const getDefaultItem = (type: itemType = itemType.equip): Item => {
	const base: Item = {
		item_id: undefined,
		name: '',
		description: '',
		tag: '',
		type,
		credits: 100,
		uses: 0,
		usesType: '',
		usesSizeMax: '',
		quality: 6,
		useActions: 1,
		rangeStd: 0,
		rangeMax: 0,
		equipSlot: 0,
		webURL: '',
		imageURL: '',
		tokenUrl: '',
		rawImage: '',
		imageSource: 'family',
		glyphURL: '',
		itemGrade: itemGrade.personal,
		secLevel: 0,
		itemSize: itemSize.small,
		sizeScale: 1,
		drainCadence: drainCadence.passive,
		drain: 0,
		selfBurnMinutes: 0,
		starterCellId: '',
		maxDosesPerOp: 0,
		dice: '',
		diceDescription: '',
		attributes: [],
		property: [],
		tokenId: '',
		mark: 0,
		disabled: false,
		disableDuration: 0,
		icon_id: 0,
		public: false,
		createdBy: '',
		isContainer: false,
		containerCapacity: 0,
		quickAccess: false,
		tags: [],
		enablesSlots: null,
		equippingHides: false,
		occupiesSlots: [],
		occupiesSlotsHelp: '',
	};
	if (type === itemType.weapon) {
		base.weaponType = weaponType.slug;
		base.weaponClass = weaponClass.standard;
		base.damageType = weaponDamage.blunt;
		base.hitRating = 1;
		base.hits = '';
		base.reloadActions = 1;
		base.targetsMax = 1;
		base.altShots = 1;
	}
	return base;
};

/**
 * Strip type-irrelevant fields from an Item before persistence — paired
 * with `getDefaultItem`'s type-aware initialisation so that any path
 * which mutates an item's `type` (editor type-selector flip, import
 * script, programmatic update) ends up with a clean Firestore document.
 *
 * Currently strips weapon-only fields on non-weapons. Extend here when
 * other type-specific fields show up (e.g. armour-only, container-only).
 *
 * Returns a NEW object — does not mutate the input.
 */
export const stripIrrelevantFieldsForType = (item: Item): Item => {
	const out: any = { ...item };
	if (item.type !== itemType.weapon) {
		delete out.weaponType;
		delete out.weaponClass;
		delete out.damageType;
		delete out.hitRating;
		delete out.hits;
		delete out.reloadActions;
		delete out.targetsMax;
		delete out.altShots;
		delete out.aoe;
	}
	return out as Item;
};

/**
 * True when an item is tagged `draft` (work-in-progress). Draft items are
 * kept out of the Foundry export bundle so unfinished gear never reaches a
 * game. Checks both the `tags[]` array and the legacy comma/space `tag`
 * string, case-insensitively — matching how the Itemizer surfaces tags.
 */
export const isDraftItem = (item: Pick<Item, 'tags' | 'tag'>): boolean => {
	const inArray = (item.tags ?? []).some((t) => String(t).toLowerCase().trim() === 'draft');
	const inString = String(item.tag ?? '')
		.toLowerCase()
		.split(/[,\s]+/)
		.map((s) => s.trim())
		.includes('draft');
	return inArray || inString;
};
